import { XMLParser } from "fast-xml-parser";
import { FetchXmlError } from "./errors.js";
import { type FetchAttributes, type FetchElement, type FetchElementName } from "./model.js";
import { validateRoot } from "./validation.js";

function isObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object";
}

function parseAttributes(input: Record<string, unknown>): FetchAttributes {
  const attrs: FetchAttributes = {};
  for (const [key, value] of Object.entries(input)) {
    if (!key.startsWith("@_")) continue;
    attrs[key.substring(2)] = value as string | number | boolean;
  }
  return attrs;
}

function parseElement(name: string, raw: unknown, path: string): FetchElement {
  if (!isObject(raw)) {
    return {
      name: name as FetchElementName,
      attributes: {},
      children: [],
      text: String(raw),
    };
  }

  const attributes = parseAttributes(raw);
  const children: FetchElement[] = [];
  let text: string | undefined;

  for (const [key, value] of Object.entries(raw)) {
    if (key.startsWith("@_")) continue;
    if (key === "#text") {
      text = String(value);
      continue;
    }

    if (Array.isArray(value)) {
      value.forEach((child, index) => {
        children.push(parseElement(key, child, `${path}/${key}[${index}]`));
      });
      continue;
    }

    children.push(parseElement(key, value, `${path}/${key}[0]`));
  }

  const node: FetchElement = {
    name: name as FetchElementName,
    attributes,
    children,
    text,
  };

  // Some FetchXML producers emit both @value and <value> for conditions.
  // Canonicalize to child values to keep strict validation deterministic.
  if (node.name === "condition" && "value" in node.attributes) {
    const hasValueChildren = node.children.some((child) => child.name === "value");
    if (hasValueChildren) {
      delete node.attributes.value;
    }
  }

  return node;
}

export function fromFetchXml(xml: string): FetchElement {
  try {
    const parser = new XMLParser({
      ignoreAttributes: false,
      attributeNamePrefix: "@_",
      textNodeName: "#text",
      parseTagValue: true,
      parseAttributeValue: true,
      trimValues: true,
    });

    const parsed = parser.parse(xml);
    if (!isObject(parsed) || !isObject(parsed.fetch)) {
      throw new FetchXmlError("invalid-root", "Root <fetch> element is required", "root");
    }

    const node = parseElement("fetch", parsed.fetch, "fetch");
    validateRoot(node);
    return node;
  } catch (error) {
    if (error instanceof FetchXmlError) {
      throw error;
    }
    throw new FetchXmlError("parse-error", `Failed to parse FetchXML: ${String(error)}`, "root");
  }
}
